import clsx from "clsx";
import { useEffect, useState } from "react";
import {
  Alert,
  CloseButton,
  Form,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import {
  DocumentSubType,
  type MiscContent,
  MiscContentType,
  Role,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import CustomEditor from "@/components/Editor/CustomEditor";
import LeavingGuard from "@/components/LeavingGuard";
import { FormModal } from "@/components/Modals";
import PlaceholderTutorialLink from "@/components/PlaceholderTutorialLink";
import { useAuth } from "@/hooks/useAuth";
import { CaliforniaId } from "@/lib/constants";
import styles from "./PreliminaryStatement.module.scss";

interface Props {
  preliminaryStatements: MiscContent[];
  onUpdate: (update: MiscContent) => Promise<MiscContent>;
}

export default function PreliminaryStatement({
  preliminaryStatements,
  onUpdate,
}: Props) {
  const [content, setContent] = useState("");
  const { user, firm, defaultJurisdiction } = useAuth();
  const [isSharedWithFirm, setIsSharedWithFirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDirtyConfirm, setShowDirtyConfirm] = useState(false);
  const [nextSubType, setNextSubType] = useState<DocumentSubType | undefined>();
  const [toastMessage, setToastMessage] = useState("");
  const [selectedStatement, setSelectedStatement] =
    useState<MiscContent | null>();
  const [selectedStatementSubType, setSelectedStatementSubType] = useState<
    DocumentSubType | undefined
  >();

  const canSetFirmStandard = user?.role?.includes(Role.FirmAdmin);

  let subTypes: { [key: number]: string } = {
    1: "Interrogatories",
    2: "Requests for Production",
    3: "Requests for Admission",
  };

  if (CaliforniaId === defaultJurisdiction?.id) {
    subTypes = {
      1: "Special Interrogatories",
      2: "Requests for Production",
      3: "Requests for Admission",
      5: "Form Interrogatories",
    };
  }

  useEffect(() => {
    // Initialize with default statement if no subtype is selected
    if (!selectedStatementSubType && !selectedStatement) {
      const defaultStatement = preliminaryStatements.find(
        (x) => !x.documentSubType,
      );
      if (defaultStatement) {
        setSelectedStatement(defaultStatement);
        setContent(defaultStatement.content || "");
        setIsSharedWithFirm(!!defaultStatement.sharedWithFirmId);
        setIsDirty(false);
      }
    }
  }, [preliminaryStatements, selectedStatementSubType, selectedStatement]);

  useEffect(() => {
    // Update content when selectedStatement changes (but not during initial load)
    if (selectedStatement?.content !== undefined) {
      setContent(selectedStatement.content || "");
      setIsSharedWithFirm(!!selectedStatement.sharedWithFirmId);
      setIsDirty(false);
    }
  }, [selectedStatement]);

  function handleChangeContent(val: string) {
    if (val !== content) {
      setContent(val);
      checkIsDirty();
    }
  }

  async function onSave() {
    if (selectedStatement) {
      const update = {
        ...selectedStatement,
        content: content,
        sharedWithFirmId: isSharedWithFirm ? firm?.id : undefined,
      };
      const result = await onUpdate(update);
      setToastMessage("");
      setToastMessage("Preliminary Statement Saved!");
      setIsDirty(false);
      setSelectedStatement(result);
    }
  }

  function handleChangeSelectedSubType(subType: DocumentSubType | undefined) {
    if (!isDirty) {
      handleSetSelectedStatementSubType(subType);
    } else {
      setNextSubType(subType);
      setShowDirtyConfirm(true);
    }
  }

  function handleSetSelectedStatementSubType(
    subType: DocumentSubType | undefined,
  ) {
    const next = subType ?? nextSubType;
    const statement = preliminaryStatements.find(
      (x) => x.documentSubType === next,
    );
    setSelectedStatement(statement);
    setSelectedStatementSubType(next);
    setShowDirtyConfirm(false);
    setNextSubType(undefined);
    setIsDirty(false);
  }

  function handleCreateOverrideClick() {
    const defaultText = preliminaryStatements.find((x) => !x.documentSubType);

    setSelectedStatement({
      content: defaultText?.content,
      documentType: defaultText?.documentType,
      type: MiscContentType.PreliminaryStatement,
      documentSubType: selectedStatementSubType,
    });
  }

  function handleChangeCancel() {
    setContent(selectedStatement?.content || "");
    setIsSharedWithFirm(!!selectedStatement?.sharedWithFirmId);
    setIsDirty(false);
  }

  function handleFirmStandardClick(checked: boolean) {
    setIsSharedWithFirm(checked);
    checkIsDirty();
  }

  function checkIsDirty() {
    const dirty =
      content !== selectedStatement?.content ||
      isSharedWithFirm !== !!selectedStatement?.sharedWithFirmId;
    setIsDirty(dirty);
  }

  return (
    <>
      <LeavingGuard
        when={isDirty}
        message="You have unsaved changes, continue and lose them?"
        title="Unsaved changes!"
      />
      <div
        className={`${styles.stepContainer} ${styles.preliminaryStatementContainer}`}
        style={{ gap: 0 }}
      >
        <ToastContainer
          className={styles.preliminaryStatementToastContainer}
          position="top-end"
        >
          <Toast
            onClose={() => setToastMessage("")}
            show={!!toastMessage}
            delay={2500}
            bg="success"
            autohide
          >
            <Toast.Body>
              <div className={styles.preliminaryStatementToastBody}>
                <div>{toastMessage}</div>
                <CloseButton
                  className={styles.preliminaryStatementToastClose}
                  onClick={() => setToastMessage("")}
                />
              </div>
            </Toast.Body>
          </Toast>
        </ToastContainer>

        <div className={styles.preliminaryStatementSidebar}>
          {
            // biome-ignore lint/a11y/useKeyWithClickEvents: replace with button
            <div
              key={"default"}
              onClick={() => {
                handleChangeSelectedSubType(undefined);
              }}
              className={clsx(styles.libraryObjection, {
                [styles.libraryObjectionSelected]: !selectedStatementSubType,
              })}
            >
              Default
            </div>
          }
          {Object.keys(DocumentSubType).map((o) => {
            const i = Number(o);
            const t = subTypes[i as keyof typeof subTypes];

            return (
              t && (
                // biome-ignore lint/a11y/useKeyWithClickEvents: replace with button
                <div
                  key={o}
                  onClick={() => {
                    handleChangeSelectedSubType(i);
                  }}
                  className={clsx(styles.libraryObjection, {
                    [styles.libraryObjectionSelected]:
                      i === selectedStatementSubType,
                  })}
                >
                  {t}
                </div>
              )
            );
          })}
        </div>
        <div className={styles.preliminaryStatementContent}>
          {selectedStatement ? (
            <>
              {!canSetFirmStandard && !!selectedStatement.sharedWithFirmId && (
                <Alert variant="info">
                  {
                    "This Preliminary Statement is set as your Firm's Standard, contact your Firm Admin if you need to make changes."
                  }
                </Alert>
              )}

              <Form.Group className={styles.preliminaryStatementFormGroup}>
                <Form.Label>Content</Form.Label>
                <CustomEditor
                  handleChange={handleChangeContent}
                  editorContent={content}
                  showAdvancedOptions={true}
                  showMenu={true}
                  isDisabled={
                    !canSetFirmStandard && !!selectedStatement.sharedWithFirmId
                  }
                  isFullWidth={true}
                  isFullHeight={true}
                />
                {canSetFirmStandard && (
                  <Form.Check
                    id={"sharedwithfirm"}
                    type={"checkbox"}
                    label="Use as Firm Standard"
                    checked={isSharedWithFirm}
                    onChange={(e) => handleFirmStandardClick(e.target.checked)}
                  />
                )}
                <FormModal
                  title={"Abandon changes?"}
                  cancelText="Cancel"
                  confirmText="Yes, abandon"
                  show={showDirtyConfirm}
                  onClose={() => setShowDirtyConfirm(false)}
                  onConfirm={() =>
                    handleSetSelectedStatementSubType(nextSubType)
                  }
                >
                  <div>
                    Are you sure you want to abandon your saved changes?
                  </div>
                </FormModal>
              </Form.Group>
              <PlaceholderTutorialLink />
              <div className={styles.preliminaryStatementButtonGroup}>
                <Button
                  variant="default"
                  onClick={onSave}
                  disabled={!isDirty && !!selectedStatement.id}
                >
                  Save
                </Button>
                <Button
                  variant="link"
                  onClick={handleChangeCancel}
                  disabled={!isDirty && !!selectedStatement.id}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div>
              <p>
                This Document Type will use the Default Preliminary Statement.
              </p>
              <Button onClick={handleCreateOverrideClick}>Create New</Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
